/**
 * PrescriptionsBucket — Smart Inline Review
 * Auto-loads medication defaults · Review not data-entry
 * Includes: SmartDxLink, MedReviewCard, PrescriptionsBucket
 */
import React from 'react';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Pill, Plus, X, Check, Loader2, Send, Clock, AlertCircle, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { BucketHeader, EmptyBucket } from './shared';

/* ═══════════════════════════════════════
   PRESCRIPTIONS BUCKET — Smart Inline Review
   Auto-loads medication defaults · Review not data-entry
   ═══════════════════════════════════════ */

const COMMON_SIGS = [
  'Take 1 tablet by mouth once daily',
  'Take 1 tablet by mouth twice daily (BID)',
  'Take 1 tablet by mouth three times daily (TID)',
  'Take 1 tablet by mouth at bedtime (QHS)',
  'Take 1 tablet by mouth every 12 hours',
  'Take 1 tablet by mouth as needed',
  'Take 2 tablets by mouth once daily',
  'Inject subcutaneously once weekly',
  'Inject subcutaneously once daily',
  'Apply topically as directed',
  'As prescribed',
];
const COMMON_QTYS = ['15', '20', '28', '30', '60', '90', '120'];
const COMMON_REFILLS = ['0', '1', '2', '3', '6', '11'];

// Route-specific quantity options
const ROUTE_QTYS = {
  injectable: ['1 mL', '5 mL', '10 mL vial', '1 vial', '2 vials', '2 x 10 mL vials'],
  topical:    ['1 tube (30g)', '1 tube (60g)', '1 tube (100g)', '1 pump bottle', '2 pump bottles', '30 patches', '60 patches'],
  inhaled:    ['1 inhaler', '2 inhalers', '3 inhalers', '1 nebulizer solution'],
  ophthalmic: ['1 bottle (5mL)', '2 bottles', '1 tube (3.5g)'],
  oral:       COMMON_QTYS,
};

// Route-specific sig templates
const ROUTE_SIG_TEMPLATES = {
  injectable: [
    'Inject Xmg IM every 2 weeks',
    'Inject Xmg IM every week',
    'Inject Xmg IM every 3 weeks',
    'Inject Xmg IM every month',
    'Inject Xmg SubQ once daily',
    'Inject Xmg SubQ once weekly',
    'Inject as directed by provider',
  ],
  topical: [
    'Apply to affected area once daily',
    'Apply to affected area twice daily (BID)',
    'Apply thin layer to affected area BID',
    'Apply to shoulders/upper arms once daily',
    'Apply as directed',
  ],
  inhaled: [
    'Inhale 1 puff twice daily (BID)',
    'Inhale 2 puffs every 4-6 hours PRN',
    'Inhale 2 puffs before exercise',
    'Inhale as directed',
  ],
};

/* ═══════════════════════════════════════
   SMART DX LINK — Advisory diagnosis auto-linking
   Never blocking. 3 tiers: auto-linked (green), suggestion (amber), empty (gray hint)
   ═══════════════════════════════════════ */

const DX_SOURCE_LABELS = {
  chart_match: 'Matched patient chart',
  encounter_match: 'Matched encounter DX',
  soap_match: 'Found in SOAP note',
  known_indication: 'Known indication',
  pass_1_encounter_dx: 'Matched encounter DX',
  pass_1_active_problems: 'Matched patient chart',
  pass_1_problem_list: 'Matched problem list',
  pass_2_soap_note: 'Found in SOAP note A&P',
  pass_3_known_indication: 'Common indication',
  existing: 'Already linked',
};

export function SmartDxLink({ cu, dxSuggestion, enc, outputs, encounterId, setEncounter }) {
  const linked = cu.linked_diagnosis || cu.medication_linked_dx;
  const [localLinked, setLocalLinked] = React.useState(linked || '');
  const [saving, setSaving] = React.useState(false);

  // Auto-apply if suggestion has high confidence auto_linked
  React.useEffect(() => {
    if (dxSuggestion?.auto_linked && !localLinked) {
      const applyAutoLink = async () => {
        try {
          await api.post(`/encounters/${encounterId}/link-diagnosis`, {
            entity_type: 'medication', entity_id: cu.id, diagnosis_code: dxSuggestion.auto_linked
          });
          setLocalLinked(dxSuggestion.auto_linked);
          setEncounter(prev => {
            const next = { ...prev };
            const outs = { ...(next.encounter_outputs || {}) };
            const cus = [...(outs.chart_updates || [])];
            const idx = cus.findIndex(c => c.id === cu.id);
            if (idx >= 0) cus[idx] = { ...cus[idx], linked_diagnosis: dxSuggestion.auto_linked };
            outs.chart_updates = cus; next.encounter_outputs = outs;
            return next;
          });
        } catch {}
      };
      applyAutoLink();
    } else if (linked) {
      setLocalLinked(linked);
    }
  }, [dxSuggestion, linked]); // eslint-disable-line

  const linkDx = async (code) => {
    setSaving(true);
    try {
      await api.post(`/encounters/${encounterId}/link-diagnosis`, { entity_type: 'medication', entity_id: cu.id, diagnosis_code: code });
      setLocalLinked(code);
      setEncounter(prev => {
        const next = { ...prev };
        const outs = { ...(next.encounter_outputs || {}) };
        const cus = [...(outs.chart_updates || [])];
        const idx = cus.findIndex(c => c.id === cu.id);
        if (idx >= 0) cus[idx] = { ...cus[idx], linked_diagnosis: code };
        outs.chart_updates = cus; next.encounter_outputs = outs;
        return next;
      });
    } catch { toast.error('Failed to link'); }
    finally { setSaving(false); }
  };

  const allDxCodes = [...new Set([
    ...(outputs?.billing_suggestion?.diagnosis_codes || []),
    ...(enc?.note?.problem_oriented_ap || []).map(p => p.icd_code).filter(Boolean),
    ...(dxSuggestion?.suggestions || []).map(s => s.code),
  ])];

  const sourceLabel = dxSuggestion?.auto_linked_confidence
    ? DX_SOURCE_LABELS[dxSuggestion.source] || DX_SOURCE_LABELS[dxSuggestion.auto_linked_confidence] || ''
    : '';

  return (
    <div className="mt-1">
      <div className="flex items-center gap-1.5">
        <label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Diagnosis Link</label>
        {/* Advisory label — not required */}
        <span className="text-[9px] text-muted-foreground/60 italic">optional — recommended</span>
        {saving && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground ml-auto" />}
      </div>

      {localLinked ? (
        /* ── Tier 1: Linked (green) ── */
        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-[10px] font-semibold text-emerald-700">
            <Check className="w-3 h-3" />
            <span className="font-mono">{localLinked}</span>
          </span>
          {dxSuggestion?.auto_linked_desc && localLinked === dxSuggestion.auto_linked && (
            <span className="text-[10px] text-emerald-600">{dxSuggestion.auto_linked_desc}</span>
          )}
          {sourceLabel && (
            <span className="text-[9px] text-muted-foreground/50 ml-1">· {sourceLabel}</span>
          )}
          <button onClick={() => { setLocalLinked(''); linkDx(''); }}
            className="text-[9px] text-muted-foreground hover:text-foreground ml-auto">
            change
          </button>
        </div>
      ) : (
        <div className="mt-1 space-y-1.5">
          {/* ── Tier 2: Suggestions (amber) ── */}
          {(dxSuggestion?.suggestions || []).length > 0 && (
            <div>
              <p className="text-[9px] text-amber-600 font-medium mb-1">Suggested — click to link:</p>
              <div className="flex gap-1.5 flex-wrap">
                {(dxSuggestion.suggestions).slice(0, 4).map(s => (
                  <button key={s.code} onClick={() => linkDx(s.code)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-amber-200 bg-amber-50 hover:bg-amber-100 text-[10px] font-medium text-amber-800 transition-colors"
                    data-testid={`dx-suggest-${s.code}`}>
                    <span className="font-mono">{s.code}</span>
                    <span className="text-[9px] text-amber-600 font-normal">— {s.desc}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* ── Tier 3: Manual fallback ── */}
          <div className="flex items-center gap-1.5">
            {allDxCodes.length > 0 && (
              <select defaultValue="" onChange={e => { if (e.target.value) linkDx(e.target.value); }}
                className="text-[10px] border rounded px-2 py-1 bg-background text-muted-foreground"
                data-testid="dx-manual-select">
                <option value="">+ Link diagnosis from encounter...</option>
                {allDxCodes.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            <span className="text-[9px] text-muted-foreground/50 italic">
              {(dxSuggestion?.suggestions || []).length === 0 && allDxCodes.length === 0
                ? 'No diagnosis in chart yet — add in Coding tab first'
                : ''}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export function MedReviewCard({ cu, i, enc, outputs, encounterId, setEncounter, dxSuggestion, safetyFlags }) {
  const actionLabel = (cu.medication_action || 'new').replace(/_/g, ' ');
  const isHandled = cu.disposition && cu.disposition !== 'unreviewed';

  const [dose, setDose] = React.useState(cu.medication_details || cu.medication_dose || '');
  const [sig, setSig] = React.useState(cu.medication_sig || '');
  const [qty, setQty] = React.useState(cu.medication_quantity || '30');
  const [refills, setRefills] = React.useState(cu.medication_refills || '1');
  const [pharmacy, setPharmacy] = React.useState(cu.medication_pharmacy || '');
  const [defaultsLoaded, setDefaultsLoaded] = React.useState(false);
  const [defaultStrengths, setDefaultStrengths] = React.useState([]);
  const [medRoute, setMedRoute] = React.useState('oral');         // route-aware UI
  const [medQtyOptions, setMedQtyOptions] = React.useState([]);   // from database
  const [medAltSigs, setMedAltSigs] = React.useState([]);
  const [executing, setExecuting] = React.useState(null);

  // Auto-load smart defaults when card mounts
  React.useEffect(() => {
    const medName = cu.medication_name;
    if (!medName || defaultsLoaded) return;
    api.get(`/medications/${encodeURIComponent(medName)}/defaults`).then(r => {
      const d = r.data.defaults;
      const source = r.data.source || 'local';
      if (d) {
        if (!dose && d.default_dose) setDose(d.default_dose);
        else if (!dose && d.strengths?.length) setDose(d.strengths[0]);
        if (!sig && d.default_sig) setSig(d.default_sig);
        if ((!qty || qty === '30') && d.default_qty) setQty(String(d.default_qty));
        if (refills === '1' && d.default_refills !== undefined) setRefills(String(d.default_refills));
        if (d.strengths?.length) setDefaultStrengths(d.strengths);
        if (d.route) setMedRoute(d.route);
        if (d.qty_options?.length) setMedQtyOptions(d.qty_options);
        if (d.alt_sigs?.length) setMedAltSigs(d.alt_sigs);
        if (source === 'rxnorm') {
          console.info(`[RxNorm] Loaded "${medName}" from live NLM database`);
        }
      }
      setDefaultsLoaded(true);
    }).catch(() => setDefaultsLoaded(true));
  }, [cu.medication_name]); // eslint-disable-line

  const saveEdits = async () => {
    try {
      await api.put(`/encounters/${encounterId}/medications/${cu.id}/edit`, { dose, sig, quantity: qty, refills, pharmacy });
    } catch {}
  };

  const execute = async (action) => {
    setExecuting(action);
    try {
      const res = await api.post(`/encounters/${encounterId}/medications/${cu.id}/execute`, {
        medication_id: cu.id,
        action,
        edits: { dose, sig, quantity: qty, refills, pharmacy },
      });
      setEncounter(res.data.encounter);
      const labels = { send: 'Sent', queue: 'Queued for refill', add_to_list: 'Added to med list', dismiss: 'Dismissed' };
      toast.success(`${cu.medication_name} — ${labels[action] || action}`);
    } catch { toast.error('Action failed'); }
    finally { setExecuting(null); }
  };

  const dxCodes = (outputs?.billing_suggestion?.diagnosis_codes || [])
    .concat((enc?.note?.problem_oriented_ap || []).map(p => p.icd_code).filter(Boolean));
  const uniqueDx = [...new Set(dxCodes)];

  const actionColor = actionLabel === 'new' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : actionLabel === 'continued' ? 'bg-sky-50 text-sky-700 border-sky-200'
    : actionLabel === 'dose change' ? 'bg-amber-50 text-amber-700 border-amber-200'
    : actionLabel === 'discontinued' ? 'bg-red-50 text-red-700 border-red-200'
    : 'bg-muted text-muted-foreground border-border';

  const dispositionBadge = {
    queued: { cls: 'bg-amber-50 text-amber-700', icon: <Clock className="w-3 h-3" />, label: 'Queued for Refill' },
    approve: { cls: 'bg-emerald-50 text-emerald-700', icon: <Check className="w-3 h-3" />, label: 'Sent' },
    sent: { cls: 'bg-emerald-50 text-emerald-700', icon: <Check className="w-3 h-3" />, label: 'Sent' },
    added_to_list: { cls: 'bg-violet-50 text-violet-700', icon: <Plus className="w-3 h-3" />, label: 'Added to Med List' },
    reject: { cls: 'bg-muted text-muted-foreground', icon: <X className="w-3 h-3" />, label: 'Dismissed' },
  }[cu.disposition] || null;

  return (
    <div className={`rounded-xl border p-4 space-y-3 transition-all ${
        isHandled ? 'bg-muted/20 opacity-75' :
        safetyFlags?.highest_level === 'red' ? 'bg-red-50/30 border-red-300 shadow-sm' :
        safetyFlags?.highest_level === 'amber' ? 'bg-amber-50/20 border-amber-200 shadow-sm' :
        'bg-card shadow-sm'
      }`}
      data-testid={`finalize-med-${i}`}>

      {/* Header */}
      <div className="flex items-center gap-2">
        <Badge className={`text-[9px] h-5 px-2 border ${actionColor}`}>
          {actionLabel === 'new' ? 'Start' : actionLabel === 'continued' ? 'Refill' : actionLabel === 'discontinued' ? 'Stop' : actionLabel}
        </Badge>
        <span className="text-sm font-semibold flex-1">{cu.medication_name || cu.description}</span>
        {!defaultsLoaded && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
        {dispositionBadge && (
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${dispositionBadge.cls}`}>
            {dispositionBadge.icon} {dispositionBadge.label}
          </span>
        )}
      </div>

      {/* Safety Alerts — allergy + contraindications */}
      {safetyFlags?.has_alert && (
        <div className="space-y-1.5" data-testid={`safety-alerts-${i}`}>
          {/* Allergy Alert */}
          {safetyFlags.allergy_alert && (
            <div className={`flex items-start gap-2 px-3 py-2 rounded-md border ${
              safetyFlags.allergy_alert.level === 'red'
                ? 'bg-red-50 border-red-300 text-red-800'
                : 'bg-amber-50 border-amber-300 text-amber-800'
            }`} data-testid={`allergy-alert-${i}`}>
              <AlertCircle className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${safetyFlags.allergy_alert.level === 'red' ? 'text-red-500' : 'text-amber-500'}`} />
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider mb-0.5">
                  {safetyFlags.allergy_alert.type === 'allergy_exact' ? 'Allergy Alert' : 'Cross-Reactivity Alert'}
                </p>
                <p className="text-[11px]">{safetyFlags.allergy_alert.message}</p>
                {safetyFlags.allergy_alert.reaction && (
                  <p className="text-[10px] opacity-80 mt-0.5">Reaction: {safetyFlags.allergy_alert.reaction} ({safetyFlags.allergy_alert.severity})</p>
                )}
              </div>
            </div>
          )}
          {/* Contraindication Warnings */}
          {(safetyFlags.contraindications || []).map((contra, ci) => (
            <div key={ci} className={`flex items-start gap-2 px-3 py-2 rounded-md border ${
              contra.level === 'red' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-800'
            }`} data-testid={`contraindication-${i}-${ci}`}>
              <AlertCircle className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${contra.level === 'red' ? 'text-red-500' : 'text-amber-500'}`} />
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider mb-0.5">
                  Caution: {contra.condition}
                </p>
                <p className="text-[11px]">{contra.message}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Inline review form — always visible, pre-populated */}
      {!isHandled && (
        <div className="grid grid-cols-2 gap-2">
          {/* Dose */}
          <div>
            <label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Dose / Strength</label>
            {defaultStrengths.length > 0 ? (
              <select value={dose} onChange={e => setDose(e.target.value)} onBlur={saveEdits}
                className="w-full mt-0.5 text-xs border rounded-md px-2 py-1.5 bg-background"
                data-testid={`med-dose-${i}`}>
                {defaultStrengths.map(s => <option key={s} value={s}>{s}</option>)}
                <option value={dose || ''}>{dose || 'Custom...'}</option>
              </select>
            ) : (
              <input type="text" value={dose} onChange={e => setDose(e.target.value)} onBlur={saveEdits}
                placeholder="e.g. 20mg" className="w-full mt-0.5 text-xs border rounded-md px-2 py-1.5 bg-background"
                data-testid={`med-dose-${i}`} />
            )}
          </div>

          {/* Quantity — route-aware options */}
          <div>
            <label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">
              Quantity {medRoute !== 'oral' && <span className="text-blue-500 font-normal capitalize">({medRoute})</span>}
            </label>
            {medQtyOptions.length > 0 ? (
              <select value={medQtyOptions.includes(qty) ? qty : '__other'}
                onChange={e => { if (e.target.value !== '__other') { setQty(e.target.value); saveEdits(); } }}
                className="w-full mt-0.5 text-xs border rounded-md px-2 py-1.5 bg-background"
                data-testid={`med-qty-${i}`}>
                {medQtyOptions.map(q => <option key={q} value={q}>{q}</option>)}
                {!medQtyOptions.includes(qty) && qty && <option value={qty}>{qty}</option>}
                <option value="__other">Custom...</option>
              </select>
            ) : (
              <select value={COMMON_QTYS.includes(qty) ? qty : '__other'}
                onChange={e => { if (e.target.value !== '__other') setQty(e.target.value); }} onBlur={saveEdits}
                className="w-full mt-0.5 text-xs border rounded-md px-2 py-1.5 bg-background"
                data-testid={`med-qty-${i}`}>
                {(ROUTE_QTYS[medRoute] || COMMON_QTYS).map(q => (
                  <option key={q} value={q}>{q}{medRoute === 'oral' ? ' tabs' : ''}</option>
                ))}
                {!(ROUTE_QTYS[medRoute] || COMMON_QTYS).includes(qty) && qty && <option value={qty}>{qty}</option>}
              </select>
            )}
          </div>

          {/* Sig — route-aware directions */}
          <div className="col-span-2">
            <label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Sig (Directions)</label>
            <select value={sig}
              onChange={e => { setSig(e.target.value); }} onBlur={saveEdits}
              className="w-full mt-0.5 text-xs border rounded-md px-2 py-1.5 bg-background"
              data-testid={`med-sig-${i}`}>
              {/* Route-specific templates first */}
              {(ROUTE_SIG_TEMPLATES[medRoute] || []).map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
              {/* Medication-specific alt sigs from database */}
              {medAltSigs.filter(s => !(ROUTE_SIG_TEMPLATES[medRoute] || []).includes(s)).map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
              {/* Standard oral sigs as fallback */}
              {medRoute === 'oral' && COMMON_SIGS.map(s => <option key={s} value={s}>{s}</option>)}
              {/* Current value if not in any list */}
              {sig && ![...(ROUTE_SIG_TEMPLATES[medRoute]||[]), ...medAltSigs, ...COMMON_SIGS].includes(sig) && (
                <option value={sig}>{sig}</option>
              )}
            </select>
          </div>

          {/* Refills + Pharmacy */}
          <div>
            <label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Refills</label>
            <select value={refills} onChange={e => setRefills(e.target.value)} onBlur={saveEdits}
              className="w-full mt-0.5 text-xs border rounded-md px-2 py-1.5 bg-background"
              data-testid={`med-refills-${i}`}>
              {COMMON_REFILLS.map(r => <option key={r} value={r}>{r} refill{r === '1' ? '' : 's'}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Pharmacy</label>
            <input type="text" value={pharmacy} onChange={e => setPharmacy(e.target.value)} onBlur={saveEdits}
              placeholder="CVS, Walgreens..." className="w-full mt-0.5 text-xs border rounded-md px-2 py-1.5 bg-background"
              data-testid={`med-pharmacy-${i}`} />
          </div>

          {/* Smart Diagnosis Linkage */}
          {uniqueDx.length > 0 && (
            <div className="col-span-2">
              <SmartDxLink
                cu={cu}
                dxSuggestion={dxSuggestion}
                enc={enc}
                outputs={outputs}
                encounterId={encounterId}
                setEncounter={setEncounter}
              />
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      {!isHandled ? (
        <div className="flex gap-1.5 pt-1 border-t border-dashed">
          <Button size="sm" className="h-8 text-[10px] px-3 gap-1 bg-primary hover:bg-primary/90"
            onClick={() => execute('send')} disabled={!!executing}
            data-testid={`finalize-med-send-${i}`}>
            {executing === 'send' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            Send Rx
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-[10px] px-3 gap-1 border-amber-300 text-amber-700 hover:bg-amber-50"
            onClick={() => execute('queue')} disabled={!!executing}
            data-testid={`finalize-med-queue-${i}`}>
            {executing === 'queue' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Clock className="w-3 h-3" />}
            Queue Refill
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-[10px] px-3 gap-1 border-violet-300 text-violet-700 hover:bg-violet-50"
            onClick={() => execute('add_to_list')} disabled={!!executing}
            title="Record on med list without sending prescription"
            data-testid={`finalize-med-addlist-${i}`}>
            {executing === 'add_to_list' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            Add to Med List
          </Button>
          <Button size="sm" variant="ghost" className="h-8 text-[10px] px-2 text-muted-foreground ml-auto"
            onClick={() => execute('dismiss')} disabled={!!executing}
            data-testid={`finalize-med-dismiss-${i}`}>
            {executing === 'dismiss' ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
            Dismiss
          </Button>
        </div>
      ) : (
        <div className="pt-1 border-t border-dashed">
          {isHandled && dose && <p className="text-[10px] text-muted-foreground">{dose} · {sig || 'As prescribed'} · Qty {qty} · {refills} refill{refills === '1' ? '' : 's'}</p>}
        </div>
      )}
    </div>
  );
}

export function PrescriptionsBucket({ enc, outputs, encounterId, fetchEncounter, setEncounter }) {
  const meds = (outputs?.chart_updates || []).filter(cu => cu.update_type?.startsWith('medication'));
  const [dxSuggestions, setDxSuggestions] = React.useState({});
  const [safetyData, setSafetyData] = React.useState({ patient_allergies: [], medications: {} });
  const [safetyLoaded, setSafetyLoaded] = React.useState(false);

  // Load both diagnosis suggestions and safety checks in parallel on mount
  React.useEffect(() => {
    if (!encounterId || meds.length === 0 || safetyLoaded) return;
    Promise.all([
      api.get(`/encounters/${encounterId}/suggest-med-diagnoses`).catch(() => ({ data: { suggestions: {} } })),
      api.get(`/encounters/${encounterId}/medication-safety`).catch(() => ({ data: { patient_allergies: [], medications: {} } })),
    ]).then(([dxRes, safetyRes]) => {
      setDxSuggestions(dxRes.data.suggestions || {});
      setSafetyData(safetyRes.data || { patient_allergies: [], medications: {} });
      setSafetyLoaded(true);
    });
  }, [encounterId, meds.length]); // eslint-disable-line

  if (meds.length === 0) {
    return <EmptyBucket icon={Pill} message="No prescriptions captured from encounter" />;
  }

  const allergies = safetyData.patient_allergies || [];
  const allHandled = meds.every(m => m.disposition && m.disposition !== 'unreviewed');
  const totalAlerts = Object.values(safetyData.medications || {}).filter(s => s.has_alert).length;

  return (
    <div className="space-y-4" data-testid="finalize-prescriptions-bucket">
      <div className="flex items-center gap-2">
        <BucketHeader icon={Pill} title="Prescriptions" count={meds.length} status={allHandled ? 'complete' : 'pending'} />
        {totalAlerts > 0 && (
          <Badge className="text-[9px] bg-red-50 text-red-700 border-red-200 gap-0.5">
            <AlertCircle className="w-3 h-3" /> {totalAlerts} safety alert{totalAlerts > 1 ? 's' : ''}
          </Badge>
        )}
        {!allHandled && (
          <span className="text-[10px] text-muted-foreground ml-auto">Defaults auto-loaded · diagnosis auto-linked</span>
        )}
      </div>

      {/* Allergy Banner — always show at top if patient has known allergies */}
      {allergies.length > 0 && (
        <div className={`rounded-lg border px-4 py-2.5 flex items-start gap-3 ${totalAlerts > 0 ? 'bg-red-50 border-red-200' : 'bg-amber-50/60 border-amber-200'}`}
          data-testid="allergy-banner">
          <AlertCircle className={`w-4 h-4 shrink-0 mt-0.5 ${totalAlerts > 0 ? 'text-red-500' : 'text-amber-500'}`} />
          <div className="flex-1 min-w-0">
            <p className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${totalAlerts > 0 ? 'text-red-700' : 'text-amber-700'}`}>
              Known Patient Allergies
            </p>
            <div className="flex flex-wrap gap-1.5">
              {allergies.map((a, i) => (
                <span key={i} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${
                  totalAlerts > 0 ? 'bg-red-100 text-red-800 border-red-300' : 'bg-amber-100 text-amber-800 border-amber-300'
                }`} data-testid={`allergy-tag-${i}`}>
                  {a.name}
                  {a.severity && a.severity !== 'unknown' && (
                    <span className="font-normal opacity-80">· {a.severity}</span>
                  )}
                  {a.reaction && (
                    <span className="font-normal opacity-70">({a.reaction})</span>
                  )}
                </span>
              ))}
              {allergies.length === 0 && <span className="text-[10px] text-muted-foreground">NKDA</span>}
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {meds.map((cu, i) => (
          <MedReviewCard key={cu.id || i} cu={cu} i={i} enc={enc} outputs={outputs}
            encounterId={encounterId} setEncounter={setEncounter}
            dxSuggestion={dxSuggestions[cu.id]}
            safetyFlags={safetyData.medications?.[cu.id]} />
        ))}
      </div>
    </div>
  );
}


